import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { UploadData } from '@app/shared/types/models/files/upload-data.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, finalize, map, Observable, Subscription, tap } from 'rxjs';
import { FileContext, UploadStatus } from '../types/enums/file-enums';
import { FileMetadata, FileMetadataInfo } from '../types/models/files/file-metadata';
import { FileUploadInfo } from '../types/models/files/file-upload-info';
import { DataService } from './data-service';
import { FileDTO } from './dtos/file/file.dto';
import { FolderFileDTO } from './dtos/folders/folder.dto';
import { ProjectTaskDTO } from './dtos/project-tasks/project-task.dto';
import { FileFactory } from './factories/file.factory';
import { FolderFactory } from './factories/folder.factory';
import { ProjectTaskFactory } from './factories/project-task.factory';
import { FileHttpService } from './http/file-http.service';
import { SessionScopeService } from './session-scope.service';
import { ToastService } from './shared/toast.service';
import { TasksDataService } from './tasks-data.service';

export type FileUrlParams =
  | { context: FileContext.locationFile; projectId: string; locationId: string }
  | { context: FileContext.projectDraftAvatar; projectId: string }
  | { context: FileContext.taskFile; taskId: string }
  | { context: FileContext.taskDescriptionFile; taskId: string }
  | { context: FileContext.taskCommentFile; taskId: string; taskCommentId: string }
  | { context: FileContext.timeReportFile; timeReportId: string }
  | { context: FileContext.folderFile; folderId: string; taskId?: string };

/**
 * FileDataService - Cached files and uploads; company-scoped download/update when applicable.
 *
 * Uses {@link ActiveCompanyService.activeCompanyId} for company file API paths; skips or throws when no active
 * company is set.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: File lists, uploads, and download actions across task/location/project contexts.
 *
 * Architecture:
 * - {@link FileDataService}: Store and orchestration
 * - {@link FileHttpService}: HTTP helpers where extracted
 * - {@link ActiveCompanyService}: Current company for `companies/{id}/files` routes
 */
@Injectable({
  providedIn: 'root'
})
export class FileDataService extends DataService<FileDTO, FileInfo> {
  private readonly files = new DataStore<FileInfo>();
  private readonly locationFiles = new DataStore<IdsCollection>();
  private readonly taskFiles = new DataStore<IdsCollection>();
  private readonly taskDescriptionFiles = new DataStore<IdsCollection>();
  private readonly taskCommentFiles = new DataStore<IdsCollection>();
  private readonly projectAvatarsFiles = new DataStore<IdsCollection>();
  private readonly timeReportFiles = new DataStore<IdsCollection>();

  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly folderFactory = inject(FolderFactory);
  /** User + company scope; a token refresh keeps it, so late upload/delete results are still applied. */
  private readonly sessionKey = inject(SessionScopeService).key;
  private readonly uploadSessions = new Map<string, string | undefined>();
  private readonly fileFactory: FileFactory = inject(FileFactory);
  private readonly taskFactory: ProjectTaskFactory = inject(ProjectTaskFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly fileHttpService: FileHttpService = inject(FileHttpService);

  private readonly fileStores = new Map<FileContext, DataStore<IdsCollection>>([
    [FileContext.locationFile, this.locationFiles],
    [FileContext.taskFile, this.taskFiles],
    [FileContext.taskDescriptionFile, this.taskDescriptionFiles],
    [FileContext.taskCommentFile, this.taskCommentFiles],
    [FileContext.projectDraftAvatar, this.projectAvatarsFiles],
    [FileContext.timeReportFile, this.timeReportFiles]
  ]);

  private readonly activeUploads = signal<Map<string, FileUploadInfo>>(new Map());
  private readonly uploadRequests = new Map<string, Subscription>();
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly activeCompanyId: Signal<string | undefined> = this.activeCompanyService.activeCompanyId;
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);
  public getById(id: string): Signal<FileInfo | undefined> {
    if (!this.files.hasDataForId(id)) {
      this.fetchImageById(id);
    }

    return computed(() => this.files.get(id)());
  }

  public getFiles(params: FileUrlParams): Signal<FileInfo[] | undefined> {
    const entityId = this.getEntityId(params);

    if (!entityId) {
      return signal(undefined);
    }

    const store = this.fileStores.get(params.context);
    if (!store) {
      return signal(undefined);
    }

    if (!store.hasDataForId(entityId)) {
      this.fetchFiles(params);
    }
    return computed(() => {
      const filesIds = store.get(entityId)()?.ids || [];
      const files = filesIds.map(id => this.files.get(id)()).filter(file => file !== undefined) || [];
      return files.sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0));
    });
  }

  public getActiveUploads(contextId: string, context?: FileContext): Signal<FileUploadInfo[]> {
    return computed(() => {
      const session = this.sessionKey();
      const iterable = this.activeUploads().values();
      return Array.from(iterable).filter(
        upload =>
          session !== undefined &&
          this.uploadSessions.get(upload.id) === session &&
          (upload.contextId === contextId || upload.id === contextId) &&
          (context === undefined || upload.context === context)
      );
    });
  }

  public upsertLocalData(dto: FileDTO): Signal<FileInfo> {
    return dto ? this.files.upsert(this.fileFactory.produce(dto)) : signal(undefined);
  }

  public removeLocalAttachment(fileId: string): void {
    // Invalidate existing subscribers before removing the map entry; File.Id is never supplied here.
    this.files.store.get(fileId)?.set(undefined);
    this.files.delete(fileId);
    this.fileStores.forEach(store => {
      store.store.forEach(collection => {
        const current = collection();
        if (current?.ids.includes(fileId)) store.upsert({ ...current, ids: current.ids.filter(id => id !== fileId) });
      });
    });
  }

  public reloadTaskFiles(taskId: string): void {
    this.fetchFiles({
      context: FileContext.taskFile,
      taskId: taskId
    });
  }
  //
  // **** UNIVERSAL FILES OPERATIONS METHODS ****
  //

  public uploadFile(params: FileUrlParams, uploadData: UploadData, afterSave?: (file: FileDTO) => void): string {
    const contextId = this.getEntityId(params);
    const companyId = this.activeCompanyId();
    const session = this.sessionKey();
    const uploadId = `${contextId}_${crypto.randomUUID()}`;
    this.uploadSessions.set(uploadId, session);
    this.activeUploads.update(uploads => {
      const newMap = new Map(uploads);
      newMap.set(
        uploadId,
        new FileUploadInfo({
          id: uploadId,
          companyId,
          contextId,
          context: params.context,
          fileName: uploadData.file.name,
          status: UploadStatus.Started
        })
      );
      return newMap;
    });

    const formData = this.createUploadFormData(uploadData);
    const uploadUrl = this.getFileUrl(params);

    const source: Observable<HttpEvent<FileDTO[] | FolderFileDTO[]>> =
      params.context === FileContext.folderFile
        ? this.fileHttpService.uploadFolderFiles(params.folderId, formData)
        : this.fileHttpService.uploadFiles(uploadUrl, formData);
    const request = source.pipe(finalize(() => this.uploadRequests.delete(uploadId))).subscribe({
      next: (event: HttpEvent<FileDTO[] | FolderFileDTO[]>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          const progress = Math.round((100 * event.loaded) / event.total);

          this.activeUploads.update(uploads => {
            const newMap = new Map(uploads);
            const current = newMap.get(uploadId);
            if (current) {
              newMap.set(
                uploadId,
                new FileUploadInfo({
                  ...current,
                  progress
                })
              );
            }
            return newMap;
          });
        } else if (event.type === HttpEventType.Response) {
          let file: FileDTO | undefined;
          try {
            const dto = event.body?.[0];
            file =
              dto && params.context === FileContext.folderFile
                ? this.folderFactory.produceFileDTO(dto as FolderFileDTO)
                : (dto as FileDTO | undefined);
          } catch {
            this.markUploadFailed(uploadId, this.translateService.instant('files.file-upload-invalid-response'));
            return;
          }
          if (!file?.id || event.body?.length !== 1) {
            this.markUploadFailed(uploadId, this.translateService.instant('files.file-upload-invalid-response'));
            return;
          }
          const stillCurrent = session === this.sessionKey();
          if (stillCurrent) {
            this.upsertLocalData(file);
            this.updateDataStore(params, file.id, 'add');
            afterSave?.(file);
            if (
              params.context === FileContext.taskFile ||
              params.context === FileContext.taskDescriptionFile ||
              params.context === FileContext.taskCommentFile
            ) {
              this.notificationService.notify({
                eventType: 'TaskFilesChanged',
                data: { taskId: params.taskId, companyId }
              });
            }
          }

          if (
            stillCurrent &&
            (params.context === FileContext.taskFile ||
              params.context === FileContext.taskDescriptionFile ||
              params.context === FileContext.folderFile) &&
            params.taskId
          ) {
            this.tasksDataService.refreshTask(params.taskId);
          }

          this.activeUploads.update(uploads => {
            const newMap = new Map(uploads);
            newMap.set(
              uploadId,
              new FileUploadInfo({
                ...newMap.get(uploadId),
                id: uploadId,
                fileName: uploadData.file.name,
                progress: 100,
                status: UploadStatus.Completed,
                fileIds: [file.id]
              })
            );
            return newMap;
          });

          if (stillCurrent)
            this.toastService.success(this.translateService.instant('files.file-upload-success', { value: file.name }));
        }
      },
      error: (error: unknown) => {
        this.markUploadFailed(uploadId, error instanceof HttpErrorResponse ? error.message : 'Unknown error');
      },
      complete: () => {
        if (this.activeUploads().get(uploadId)?.status === UploadStatus.Started) {
          this.markUploadFailed(uploadId, this.translateService.instant('files.file-upload-invalid-response'));
        }
      }
    });

    if (!request.closed) {
      this.uploadRequests.set(uploadId, request);
    }

    return uploadId;
  }

  private markUploadFailed(uploadId: string, error: string): void {
    this.activeUploads.update(uploads => {
      const current = uploads.get(uploadId);
      if (!current) {
        return uploads;
      }
      return new Map(uploads).set(uploadId, new FileUploadInfo({ ...current, status: UploadStatus.Failure, error }));
    });
  }

  /** Cancels this HTTP request; it does not guarantee rollback of a server-side write. */
  public cancelUpload(uploadId: string): void {
    const request = this.uploadRequests.get(uploadId);
    if (!request) {
      return;
    }

    request.unsubscribe();
    this.activeUploads.update(uploads => {
      const current = uploads.get(uploadId);
      if (!current) {
        return uploads;
      }
      return new Map(uploads).set(uploadId, new FileUploadInfo({ ...current, status: UploadStatus.Canceled }));
    });
  }

  public dismissUpload(uploadId: string): void {
    if (this.uploadRequests.has(uploadId)) {
      return;
    }
    this.uploadSessions.delete(uploadId);
    this.activeUploads.update(uploads => {
      const newMap = new Map(uploads);
      newMap.delete(uploadId);
      return newMap;
    });
  }

  public deleteFile(params: FileUrlParams, file: FileInfo): void {
    const fileId = file.id;
    const deleteUrl = this.getFileUrl(params, fileId);
    const companyId = this.activeCompanyId();
    const session = this.sessionKey();

    this.httpClient.delete<void>(deleteUrl).subscribe({
      next: () => {
        if (session !== this.sessionKey()) return;
        this.toastService.success(this.translateService.instant('files.file-delete-success', { value: file.name }));
        this.removeLocalAttachment(fileId);
        this.updateDataStore(params, fileId, 'delete');
        if (
          params.context === FileContext.taskFile ||
          params.context === FileContext.taskDescriptionFile ||
          params.context === FileContext.taskCommentFile
        ) {
          this.notificationService.notify({
            eventType: 'TaskFilesChanged',
            data: { taskId: params.taskId, companyId }
          });
        }
      }
    });
  }

  public download(file: FileInfo): void {
    this.downloadFile(file).subscribe();
  }

  /** Fetches the file and saves it in the browser; completes once the browser download has been triggered. */
  public downloadFile(file: FileInfo): Observable<void> {
    const companyId = this.activeCompanyId();
    if (!companyId) {
      return EMPTY;
    }
    return this.fileHttpService.downloadFile(companyId, file.fileShortId).pipe(
      map(blob => this.saveInBrowser(blob, file.name || 'download')),
      catchError(() => {
        this.toastService.error(this.translateService.instant('files.file-download-fail'));
        return EMPTY;
      })
    );
  }

  public updateFile(fileId: string, metadata: FileMetadata): Observable<FileDTO> {
    const companyId = this.activeCompanyId();
    if (!companyId) {
      throw new Error('Active company is required to update a file');
    }
    return this.fileHttpService.updateFile(companyId, fileId, metadata).pipe(tap(res => this.upsertLocalData(res)));
  }

  public updateFiles(files: FileMetadataInfo[]): void {
    const companyId = this.activeCompanyId();
    if (!companyId) {
      return;
    }
    this.httpClient
      .put<FileDTO[]>(
        `${environment.APIEndPoint}companies/${companyId}/files`,
        files.map(data => ({
          ...data,
          tags: data.fileTags
        }))
      )
      .subscribe({
        next: res => {
          res.forEach(dto => this.upsertLocalData(dto));
        }
      });
  }

  //
  // **** TASKS FILES ****
  //

  public setTaskAvatar(taskId: string, fileId: string): void {
    this.httpClient
      .post<ProjectTaskDTO>(`${environment.APIEndPoint}tasks/${taskId}/files/${fileId}/set-avatar`, null)
      .subscribe({
        next: (dto: ProjectTaskDTO) => {
          if (dto) {
            this.toastService.success(
              this.translateService.instant('project-tasks.toasts.task-avatar-successfully-set')
            );
            this.tasksDataService.upsertLocalData(dto);
          }
        }
      });
  }

  //
  // **** FETCHES ****
  //
  private saveInBrowser(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private fetchImageById(fileId: string): void {
    this.httpClient.get<FileDTO>(`${environment.APIEndPoint}files/${fileId}`).subscribe({
      next: dto => {
        this.upsertLocalData(dto);
      }
    });
  }

  private fetchFiles(params: FileUrlParams): void {
    const url = this.getFileUrl(params);
    const entityId = this.getEntityId(params);
    const store = this.fileStores.get(params.context);

    if (!store || !entityId) {
      return;
    }

    this.httpClient.get<FileDTO[]>(url).subscribe({
      next: (dto: FileDTO[]) => {
        this.files.upsertMany(dto.map(itm => this.fileFactory.produce(itm)));

        store.upsert({
          id: entityId,
          ids: dto.map(itm => itm.id)
        });
      }
    });
  }

  //
  // **** HELPERS ****
  //
  private createUploadFormData(uploadData: UploadData): FormData {
    const formData = new FormData();
    formData.append('files[0].File', uploadData.file, uploadData.file.name);
    formData.append('files[0].OrderNo', uploadData.orderNo.toString());

    if (uploadData.description) {
      formData.append('files[0].Description', uploadData.description);
    }

    uploadData.fileTags?.forEach((tag, index) => {
      formData.append(`files[0].Tags[${index}]`, tag);
    });

    formData.append(
      'attachments',
      JSON.stringify([
        {
          orderNo: uploadData.orderNo,
          description: uploadData.description ?? null,
          tags: uploadData.fileTags ?? []
        }
      ])
    );

    return formData;
  }

  private updateDataStore(params: FileUrlParams, fileId: string, methodType: 'add' | 'delete'): void {
    const updateStore = (store: DataStore<IdsCollection>, id: string): void => {
      if (methodType === 'delete' && !store.hasDataForId(id)) {
        return;
      }
      const currentFilesIds = store.get(id)()?.ids || [];
      const newFilesIds =
        methodType === 'add'
          ? currentFilesIds.concat(fileId)
          : currentFilesIds.filter(existingId => existingId !== fileId);
      store.upsert({ id, ids: newFilesIds });
    };

    switch (params.context) {
      case FileContext.folderFile:
        // FolderDataService owns the containing folder's attachment IDs.
        return;
      case FileContext.locationFile:
        updateStore(this.locationFiles, params.locationId);
        return;
      case FileContext.projectDraftAvatar:
        updateStore(this.projectAvatarsFiles, params.projectId);
        return;
      case FileContext.taskFile:
        updateStore(this.taskFiles, params.taskId);
        if (methodType === 'delete') {
          updateStore(this.taskDescriptionFiles, params.taskId);
        }
        return;
      case FileContext.taskDescriptionFile:
        updateStore(this.taskDescriptionFiles, params.taskId);
        updateStore(this.taskFiles, params.taskId);
        return;
      case FileContext.taskCommentFile:
        updateStore(this.taskCommentFiles, params.taskCommentId);
        return;
      case FileContext.timeReportFile:
        updateStore(this.timeReportFiles, params.timeReportId);
        return;
      default: {
        const _exhaustiveCheck: never = params;
        throw new Error(`Unsupported file context: ${(_exhaustiveCheck as FileUrlParams).context}`);
      }
    }
  }

  private getFileUrl(params: FileUrlParams, fileId?: string): string {
    const baseUrl = this.getBaseFileUrl(params);
    return fileId ? `${baseUrl}/${fileId}` : baseUrl;
  }

  private getBaseFileUrl(params: FileUrlParams): string {
    const baseEndpoint = environment.APIEndPoint;

    switch (params.context) {
      case FileContext.folderFile:
        return `${environment.APIV3EndPoint}folders/${encodeURIComponent(params.folderId)}/files`;
      case FileContext.locationFile:
        return `${baseEndpoint}projects/${params.projectId}/locations/${params.locationId}/files`;
      case FileContext.projectDraftAvatar:
        return `${baseEndpoint}project-drafts/avatar`;
      case FileContext.taskFile:
        return `${baseEndpoint}tasks/${params.taskId}/files`;
      case FileContext.taskDescriptionFile:
        return `${baseEndpoint}tasks/${params.taskId}/description-files`;
      case FileContext.taskCommentFile:
        return `${baseEndpoint}tasks/${params.taskId}/comments/${params.taskCommentId}/files`;
      case FileContext.timeReportFile:
        return `${baseEndpoint}time-reports/${params.timeReportId}/files`;
      default: {
        const _exhaustiveCheck: never = params;
        throw new Error(`Unsupported file context: ${(_exhaustiveCheck as FileUrlParams).context}`);
      }
    }
  }

  private getEntityId(params: FileUrlParams): string {
    switch (params.context) {
      case FileContext.folderFile:
        return params.folderId;
      case FileContext.locationFile:
        return params.locationId;
      case FileContext.projectDraftAvatar:
        return params.projectId;
      case FileContext.taskFile:
      case FileContext.taskDescriptionFile:
        return params.taskId;
      case FileContext.taskCommentFile:
        return params.taskCommentId;
      case FileContext.timeReportFile:
        return params.timeReportId;
      default: {
        const _exhaustiveCheck: never = params;
        throw new Error(`Unsupported file context: ${(_exhaustiveCheck as FileUrlParams).context}`);
      }
    }
  }
}
